import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import {
  FLIPKART_BUDGET_ADJUST_MODE_LABELS,
  parseFlipkartBudgetAdjustValue,
  type FlipkartBudgetAdjustMode,
  type FlipkartCampaignBudgetJob,
  type FlipkartCampaignBudgetResult,
} from '@/api/flipkartCampaignBudgetJob';

const MODES = Object.keys(FLIPKART_BUDGET_ADJUST_MODE_LABELS) as FlipkartBudgetAdjustMode[];

export function FlipkartCampaignBudgetModal({
  selectionCount,
  pending,
  job = null,
  failedResults = [],
  error = null,
  onCancel,
  onConfirm,
  onRetryFailed,
}: {
  selectionCount: number;
  pending: boolean;
  job?: FlipkartCampaignBudgetJob | null;
  failedResults?: FlipkartCampaignBudgetResult[];
  error?: string | null;
  onCancel: () => void;
  onConfirm: (mode: FlipkartBudgetAdjustMode, value: number) => void;
  onRetryFailed?: () => void;
}) {
  const [mode, setMode] = useState<FlipkartBudgetAdjustMode>('INCREASE_AMOUNT');
  const [draft, setDraft] = useState('');
  const value = parseFlipkartBudgetAdjustValue(draft);
  const noun = selectionCount === 1 ? 'campaign' : 'campaigns';

  if (job) {
    return (
      <FlipkartCampaignBudgetProgress
        job={job}
        failedResults={failedResults}
        isRunning={pending}
        onClose={onCancel}
        onRetryFailed={onRetryFailed}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-md flex-col rounded-xl bg-surface p-5 shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          onClick={onCancel}
          disabled={pending}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="pr-8 text-base font-semibold text-foreground">
          Change budget for {selectionCount} {noun}
        </h3>
        <p className="mt-2 text-body-sm text-foreground-muted">
          Each selected campaign’s daily budget increases by the amount or percent you enter.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="flipkart-budget-mode" className="mb-1 block text-xs text-foreground-muted">
              Budget adjustment
            </label>
            <Select
              id="flipkart-budget-mode"
              className="w-full"
              value={mode}
              onChange={(event) => setMode(event.target.value as FlipkartBudgetAdjustMode)}
              aria-label="Budget adjustment"
            >
              {MODES.map((option) => (
                <option key={option} value={option}>
                  {FLIPKART_BUDGET_ADJUST_MODE_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label htmlFor="flipkart-budget-value" className="mb-1 block text-xs text-foreground-muted">
              Value
            </label>
            <Input
              id="flipkart-budget-value"
              type="number"
              min="0"
              step={mode === 'INCREASE_PERCENT' ? '1' : '0.01'}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={mode === 'INCREASE_PERCENT' ? 'Percent' : 'Amount'}
              aria-label="Budget increase value"
            />
          </div>
        </div>
        {error ? <p className="mt-3 text-body-sm text-error">{error}</p> : null}
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (value == null) return;
              onConfirm(mode, value);
            }}
            disabled={pending || value == null}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FlipkartCampaignBudgetProgress({
  job,
  failedResults,
  isRunning,
  onClose,
  onRetryFailed,
}: {
  job: FlipkartCampaignBudgetJob;
  failedResults: FlipkartCampaignBudgetResult[];
  isRunning: boolean;
  onClose: () => void;
  onRetryFailed?: () => void;
}) {
  const total = job.applyProgress.total ?? job.totals.total ?? 0;
  const processed = job.applyProgress.processed ?? 0;
  const pct =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : isRunning ? 0 : 100;
  const failed = job.totals.failed ?? 0;
  const applied = job.totals.applied ?? 0;
  const skipped = job.totals.skipped ?? 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">
          {isRunning ? 'Working on it' : 'Budget results'}
        </h3>
        <p className="mt-1 text-sm text-foreground-muted">
          {isRunning
            ? `Updating campaigns… ${processed} of ${total || '—'}`
            : job.status === 'failed'
              ? job.applyProgress.error || 'Budget job failed'
              : `Applied ${applied}, skipped ${skipped}, failed ${failed}`}
        </p>
        <div className="mt-4">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-border"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Budget update progress"
          >
            <div
              className="h-full rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-foreground-muted">{pct}%</p>
        </div>
        {!isRunning && failed > 0 ? (
          <div className="app-table-scroll mt-4 max-h-56 min-w-0 max-w-full overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Campaign</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {failedResults.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-foreground-muted" colSpan={2}>
                      {failed} campaign{failed === 1 ? '' : 's'} failed.
                    </td>
                  </tr>
                ) : (
                  failedResults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-foreground">
                        {row.campaignName || row.campaignId}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {row.errorMessage || 'Failed'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          {!isRunning && failed > 0 ? (
            <Button type="button" onClick={onRetryFailed} disabled={isRunning}>
              Retry failed
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}
